import React, { useEffect, useRef, useState } from 'react';
import { useMessageStore } from '../../store/messageStore';
import { Message } from '../../types';
import MessageBubble from './MessageBubble';

interface MessageHistoryProps {
  onRefresh?: () => void;
}

const MessageHistory: React.FC<MessageHistoryProps> = ({ onRefresh }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [lastMessageCount, setLastMessageCount] = useState(0);

  // Store state and actions
  const { 
    messages, 
    loading, 
    error, 
    totalCount, 
    wsConnected,
    refreshMessages,
    clearError,
  } = useMessageStore();

  // Auto-scroll to bottom when new messages arrive, but not for outbound messages
  useEffect(() => {
    if (messages.length > lastMessageCount) {
      const newMessages = messages.slice(lastMessageCount);
      const hasNewInboundMessage = newMessages.some(msg => msg.direction === 'inbound');
      
      // Only auto-scroll if there are inbound messages or if user is already at bottom
      if (autoScroll && messagesEndRef.current && hasNewInboundMessage) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setLastMessageCount(messages.length);
  }, [messages, autoScroll, lastMessageCount]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 5;
    setAutoScroll(isAtBottom);
  };

  const handleRefresh = () => {
    refreshMessages();
    onRefresh?.();
  };

  const groupMessagesByDate = (messages: Message[]) => {
    return messages.reduce((groups: Record<string, Message[]>, message) => {
      const date = new Date(message.created_at).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
      return groups;
    }, {});
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
  };

  const groupedMessages = groupMessagesByDate(messages);

  return (
    <div className="message-history">
      <div className="message-history__header">
        <h2>Message History</h2>
        <div className="message-history__actions">
          <div className="message-history__info">
            <span className="message-count">
              {totalCount} message{totalCount !== 1 ? 's' : ''}
            </span>
            <div className="connection-status">
              <span 
                className={`connection-indicator ${wsConnected ? 'connected' : 'disconnected'}`}
                title={wsConnected ? 'Real-time connected' : 'Real-time disconnected'}
              >
                {wsConnected ? '🟢' : '🔴'}
              </span>
            </div>
          </div>
          <button 
            onClick={handleRefresh}
            className="refresh-button"
            disabled={loading}
            type="button"
          >
            {loading ? '⟳' : '↻'} Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="message-history__error">
          <div className="error-content">
            <span className="error-icon">⚠️</span>
            <span className="error-message">{error}</span>
            <button 
              onClick={clearError}
              className="error-dismiss"
              type="button"
              aria-label="Dismiss error"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div 
        className="message-history__container"
        onScroll={handleScroll}
      >
        {loading && messages.length === 0 ? (
          <div className="message-history__loading">
            <div className="spinner"></div>
            <p>Loading messages...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="message-history__empty">
            <div className="empty-icon">💬</div>
            <h3>No messages yet</h3>
            <p>Send your first SMS to get started!</p>
          </div>
        ) : (
          <div className="message-history__messages">
            {Object.entries(groupedMessages).map(([date, dateMessages]) => (
              <div key={date} className="message-group">
                <div className="message-group__date">
                  {formatDate(date)}
                </div>
                {dateMessages.map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                  />
                ))}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {!autoScroll && messages.length > 0 && (
        <button 
          className="scroll-to-bottom"
          onClick={() => {
            setAutoScroll(true);
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }}
          type="button"
        >
          ↓ New messages
        </button>
      )}
    </div>
  );
};

export default MessageHistory;